import { Component, Input, OnInit, OnDestroy, PLATFORM_ID, EventEmitter, Output, ElementRef, HostListener, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { GtmService } from '../../../core/services/gtm.service';

export interface BannerItem {
  id: string;
  imageUrl: string;
  title?: string; // Opcional para acessibilidade
  route?: string;
  externalUrl?: string;
}

@Component({
  selector: 'umkt-banner-carousel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './banner-carousel.component.html',
  styleUrls: ['./banner-carousel.component.scss']
})
export class BannerCarouselComponent implements OnInit, OnDestroy {
  @Input() banners: BannerItem[] = [];
  @Input() autoPlay: boolean = true;
  @Input() autoPlayInterval: number = 5000; // 5 segundos
  @Input() isMobile: boolean = false;
  @Input() disableLinks: boolean = false;
  
  @Output() bannerClick = new EventEmitter<BannerItem>();

  currentIndex: number = 0;
  containerWidth: number = 0;
  private autoPlayTimer: any;
  isBrowser: boolean = false;
  // Variáveis para swipe
  private startX: number = 0;
  private startY: number = 0;
  private isDragging: boolean = false;
  private dragDistance: number = 0;
  private platformId = inject(PLATFORM_ID);
  private gtm = inject(GtmService);
  private elementRef = inject(ElementRef);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.updateContainerWidth();
    if (this.isBrowser && this.autoPlay && this.banners.length > 1) {
      this.startAutoPlay();
    }
  }

  ngOnDestroy() {
    this.stopAutoPlay();
  }

  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.banners.length;
  }

  prevSlide() {
    this.currentIndex = this.currentIndex === 0
      ? this.banners.length - 1
      : this.currentIndex - 1;
  }

  goToSlide(index: number) {
    this.currentIndex = index;
  }

  startAutoPlay() {
    this.stopAutoPlay();
    this.autoPlayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoPlayInterval);
  }

  stopAutoPlay() {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  onMouseEnter() {
    if (this.autoPlay) {
      this.stopAutoPlay();
    }
  }

  onMouseLeave() {
    if (this.autoPlay && this.banners.length > 1) {
      this.startAutoPlay();
    }
  }

  isClickable(banner: BannerItem | null): boolean {
    return !this.disableLinks && this.hasDestination(banner);
  }

  onBannerClick(banner: BannerItem) {
    if (!this.isClickable(banner)) {
      return;
    }
    this.gtm.push('select_promotion', {
      creative_name: banner.title,
      creative_slot: 'banner_carousel',
      promotion_id: banner.id,
      promotion_name: banner.title
    });
    this.bannerClick.emit(banner);
  }

  private hasDestination(banner: BannerItem | null): boolean {
    return !!(banner?.route || banner?.externalUrl);
  }

  onImageError(event: any) {
    // Fallback para imagem padrão em caso de erro
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDgwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI4MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjZjBmMGYwIi8+Cjx0ZXh0IHg9IjQwMCIgeT0iMjAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTk5IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPkltYWdlbSBuw6NvIGRpc3BvbsOtdmVsPC90ZXh0Pgo8L3N2Zz4K';
    }
  }

      // Métodos para swipe
  onTouchStart(event: TouchEvent) {
    if (!this.isBrowser || this.banners.length <= 1 || !this.shouldUseMobileBehavior) return;

    this.startX = event.touches[0].clientX;
    this.startY = event.touches[0].clientY;
    this.isDragging = true;
    this.dragDistance = 0;
  }

  onTouchMove(event: TouchEvent) {
    if (!this.isDragging || !this.shouldUseMobileBehavior) return;

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;

    const deltaX = currentX - this.startX;
    const deltaY = currentY - this.startY;

    // Verifica se o movimento é mais horizontal que vertical
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.dragDistance = deltaX;
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (!this.isDragging || !this.shouldUseMobileBehavior) return;

    const minSwipeDistance = 50; // Distância mínima para considerar como swipe

    if (Math.abs(this.dragDistance) > minSwipeDistance) {
      if (this.dragDistance > 0) {
        // Swipe para direita - slide anterior
        this.prevSlide();
      } else {
        // Swipe para esquerda - próximo slide
        this.nextSlide();
      }
    }

    this.isDragging = false;
    this.dragDistance = 0;
  }

  // Métodos para mouse drag (desktop)
  onMouseDown(event: MouseEvent) {
    if (!this.isBrowser || this.banners.length <= 1 || this.shouldUseMobileBehavior) return;

    this.startX = event.clientX;
    this.startY = event.clientY;
    this.isDragging = true;
    this.dragDistance = 0;

    event.preventDefault();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging || this.shouldUseMobileBehavior) return;

    const currentX = event.clientX;
    const currentY = event.clientY;

    const deltaX = currentX - this.startX;
    const deltaY = currentY - this.startY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      this.dragDistance = deltaX;
    }
  }

  onMouseUp(event: MouseEvent) {
    if (!this.isDragging || this.shouldUseMobileBehavior) return;

    const minSwipeDistance = 50;

    if (Math.abs(this.dragDistance) > minSwipeDistance) {
      if (this.dragDistance > 0) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
    }

    this.isDragging = false;
    this.dragDistance = 0;
  }

  get currentBanner(): BannerItem | null {
    return this.banners[this.currentIndex] || null;
  }

  get safeImageUrl(): string {
    if (!this.isBrowser || !this.currentBanner) {
      return '';
    }
    return this.currentBanner.imageUrl;
  }

  get isMobileDevice(): boolean {
    if (!this.isBrowser) return false;
    return window.innerWidth <= 768;
  }

  get shouldUseMobileBehavior(): boolean {
    return this.isMobile || this.isMobileDevice;
  }

  /** Distance to translate the track per slide (pixels). Matches --banner-peek. */
  get slideOffset(): number {
    if (!this.isBrowser || this.containerWidth === 0) return 0;
    const peek = this.shouldUseMobileBehavior ? 24 : 48;
    return this.containerWidth - peek;
  }

  @HostListener('window:resize')
  updateContainerWidth() {
    if (!this.isBrowser) return;
    const el = this.elementRef.nativeElement as HTMLElement;
    const container = el.querySelector('.banner-container') as HTMLElement | null;
    this.containerWidth = container?.offsetWidth ?? 0;
  }
}
